import { LoadingView } from "@/features/Auth/components/LoadingView";
import { usePromptbook } from "@/features/Promptbooks/hooks/usePromptbook";
import {
  Alert,
  AlertIcon,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Stack,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { v2 } from "@pufflig/prompt-studio-types";
import { Icon } from "@pufflig/prompt-studio-ui";
import { ErrorBoundary } from "@sentry/react";
import { useMemo, useState } from "react";
import { variableNameToKey } from "../../utils/variableNameToKey";
import { SchemaPreview } from "../SchemaPreview/SchemaPreview";
import { SchemaSection } from "../SchemaSection/SchemaSection";

interface DeploymentEditorDrawerProps {
  promptbookId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const DeploymentEditorDrawer = (props: DeploymentEditorDrawerProps) => {
  const { promptbookId, isOpen, onClose } = props;
  const [isDeploying, setDeploying] = useState(false);
  const [removedFields, setRemovedFields] = useState<string[]>([]);
  const toast = useToast();

  const { promptbook, isLoadingPromptbook } = usePromptbook(promptbookId);

  const editorState = promptbook?.editor_state as v2.BasePromptbookState | undefined;
  const placeholders = (editorState?.placeholders || {}) as Record<string, any>;
  const steps = editorState?.steps || {};

  const validInputs = useMemo(
    () =>
      Object.values(placeholders).map((placeholder) => {
        return {
          id: placeholder.placeholderId,
          name: placeholder.name,
          description: placeholder.description,
        };
      }),
    [placeholders],
  );

  const validOutputs = Object.values(editorState?.steps || {}).map((step) => {
    return {
      id: step.stepId,
      name: step.name,
    };
  });

  const requestSchema = validInputs.reduce(
    (acc, cur) => ({
      ...acc,
      [variableNameToKey(cur.name)]: cur.id,
    }),
    {} as { [fieldId: string]: string },
  );

  const requestSchemaTable = Object.entries(requestSchema).map(([fieldName, placeholderId]) => {
    return {
      fieldName,
      variableName: placeholders[placeholderId].name,
      type: "text",
      description: placeholders[placeholderId].description,
    };
  });

  const responseSchema = validOutputs.reduce(
    (acc, cur) => ({
      ...acc,
      [variableNameToKey(cur.name)]: cur.id,
    }),
    {} as { [fieldId: string]: string },
  );

  const responseSchemaTable = Object.entries(responseSchema)
    .filter(([key]) => {
      return !removedFields.includes(key);
    })
    .map(([fieldName, stepId]) => {
      return {
        fieldName,
        variableName: steps[stepId].name,
        type: "text",
      };
    });

  if (isLoadingPromptbook) return <LoadingView />;

  if (!promptbook) return null;

  const addFieldToSchema = async (fieldId: string) => {
    setRemovedFields((fields) => fields.filter((f) => f !== fieldId));
  };

  const removeFieldFromSchema = async (fieldId: string) => {
    setRemovedFields((fields) => [...fields, fieldId]);
  };

  const isResponseEmpty = !Object.entries(responseSchemaTable || {}).length;

  return (
    <ErrorBoundary fallback={<p>Something went wrong</p>}>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text textStyle="heading/2">New Deployment</Text>
          </DrawerHeader>
          <DrawerBody key={promptbookId}>
            <Stack gap="4">
              <SchemaSection
                title="Request Schema"
                description="The placeholders in your Promptbook that will be replaced by values from the request body."
                buttonText="Add input field"
                menuGroupText="Select input"
                variables={validInputs}
              >
                <SchemaPreview
                  fields={requestSchemaTable}
                  headers={["Field", "Placeholder Name", "Description", "Type"]}
                />
              </SchemaSection>
              <SchemaSection
                title="Response Schema"
                description="The results from the Promptbook you expect to receive as part of the response body."
                buttonText="Add output field"
                menuGroupText="Select output"
                variables={validOutputs}
                onAddField={(name) => {
                  addFieldToSchema(variableNameToKey(name));
                }}
              >
                <SchemaPreview
                  headers={["Field", "Step Name", "Description", "Type"]}
                  fields={responseSchemaTable}
                  onRemoveField={(fieldId) => removeFieldFromSchema(fieldId)}
                />
              </SchemaSection>
              <Stack
                sx={{
                  ":empty": {
                    display: "none",
                  },
                }}
              >
                {isResponseEmpty ? (
                  <Alert status="warning">
                    <AlertIcon />
                    The response schema should contain at least one field to deploy this Promptbook.
                  </Alert>
                ) : null}
              </Stack>
              <Flex>
                <Tooltip label="Make this Promptbook available via an API">
                  <Button
                    size="sm"
                    isDisabled={isResponseEmpty}
                    variant="primary"
                    leftIcon={<Icon name="arrow-up" />}
                    isLoading={isDeploying}
                    onClick={async () => {
                      if (!promptbookId) return;
                      setDeploying(true);
                      const error = true;
                      setDeploying(false);
                      if (!error) {
                        onClose();
                        toast({
                          position: "top",
                          title: "Promptbook deployed!",
                          status: "success",
                          size: "sm",
                        });
                      } else {
                        toast({
                          position: "top",
                          title: "Deployments are currently disabled",
                          status: "error",
                          size: "sm",
                        });
                      }
                    }}
                  >
                    Deploy new version
                  </Button>
                </Tooltip>
              </Flex>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </ErrorBoundary>
  );
};
